import { useState, useEffect } from 'react';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { useLocation } from 'react-router-dom';

interface UseCitySelectionPopupOptions {
  delay?: number; // Delay in milliseconds before showing popup
}

export const useCitySelectionPopup = (options: UseCitySelectionPopupOptions = {}) => {
  const { delay = 1000 } = options;
  const [showPopup, setShowPopup] = useState(false);
  const { selectedCity } = useDeliveryLocation();
  const location = useLocation();

  // Check if current route requires city selection
  const requiresCitySelection = () => {
    const path = location.pathname;
    
    // Routes that don't require city selection
    const excludedRoutes = [
      '/admin',
      '/vendor',
      '/auth',
      '/forgot-password',
      '/order-confirmation',
      '/payment-failure'
    ];

    // Check if current path starts with any excluded route
    const isExcludedRoute = excludedRoutes.some(route => path.startsWith(route));
    
    // Also exclude city-prefixed routes (they already have city context)
    const isCityPrefixedRoute = path.match(/^\/en-eg\/[^\/]+/);
    
    return !isExcludedRoute && !isCityPrefixedRoute;
  };

  useEffect(() => {
    // Check if user has recently completed an order (within last 5 minutes)
    const recentOrderTime = localStorage.getItem('lastOrderTime');
    const hasRecentOrder = recentOrderTime && (Date.now() - parseInt(recentOrderTime)) < 5 * 60 * 1000;
    
    // Show popup if no city is selected and route requires city selection
    // But don't show if user just completed an order
    if (!selectedCity && requiresCitySelection() && !hasRecentOrder) {
      // Add a delay to ensure the page has loaded
      const timer = setTimeout(() => {
        setShowPopup(true);
      }, delay);

      return () => clearTimeout(timer);
    } else {
      setShowPopup(false);
    }
  }, [selectedCity, location.pathname, delay]);

  const openPopup = () => setShowPopup(true);
  const closePopup = () => setShowPopup(false);

  return {
    showPopup,
    openPopup,
    closePopup,
    requiresCitySelection: requiresCitySelection()
  };
}; 